// Enhanced Gallery3DEditor.jsx - Adds Floor Plan View to existing 3D editor
import React, { useState, useEffect, useRef } from 'react';
import 'aframe';
import ListPanel from './ListPanel';
import ControlPanel from './ControlPanel';
import GalleryInfrastructure from './GalleryInfrastructure';
import FloorPlanView from './FloorPlanView';
import Wall3DBox, { positionUnplacedWalls } from './Wall3DBox';
import SceneSettings from './SceneSettings';
import { 
  fetchSceneElements, 
  fetchSceneElementsWithWalls,
  syncArtworkPositionsOnWall,
  updateWallPosition,
  getFloorData,
  getSceneSettings,
  saveSceneSettings
} from '../utils/api';
import { supabase } from '../utils/supabaseClient';
import '../styles/GalleryViewer.css';

/**
 * Apply Cloudinary named transform to reduce texture size for 3D rendering.
 * Uses t_800/ which resolves to c_lfill,w_800/dpr_auto/q_auto/f_webp/
 */
function get3DTextureUrl(originalUrl, show = true) {
  if (!show || !originalUrl) return ''; // empty string = no texture
  if (!originalUrl.includes('cloudinary.com') || !originalUrl.includes('/upload/')) {
    return originalUrl;
  }
  return originalUrl.replace('/upload/', '/upload/t_800/');
}

const Gallery3DEditor = ({ viewMode = '3d' }) => {
  const [galleryId, setGalleryId] = useState(null);
  const [collectionId, setCollectionId] = useState(null);
  const [sceneData, setSceneData] = useState({ 
    walls: [], 
    artworks: [], 
    boxes: [], 
    easels: [], 
    furniture: [],
    structures: []
  });
  const [floorData, setFloorData] = useState(null);
  const [selectedElement, setSelectedElement] = useState(null);
  const [selectedElements, setSelectedElements] = useState([]);
  const [showHelp, setShowHelp] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentView, setCurrentView] = useState('3d'); // NEW: '3d' or 'floor-plan'
  const [isTopDownView, setIsTopDownView] = useState(false); // Track top-down camera mode
  const [showSideMenu, setShowSideMenu] = useState(false); // Toggle for side menu (starts hidden)
  const [isSelectMode, setIsSelectMode] = useState(false); // Track selection mode
  const [showSceneSettings, setShowSceneSettings] = useState(false); // Scene settings modal
  const [isSyncing, setIsSyncing] = useState(false);
  const [sceneSettings, setSceneSettings] = useState({
    backgroundColor: '#000000',
    ambientLightIntensity: 0.6,
    directionalLightIntensity: 1.0,
    presetId: 'gallery-dark'
  });
  const sceneRef = useRef(null);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingStages, setLoadingStages] = useState({
    fetching: true,
    processing: true,
    rendering: true
  });

  // Gallery dimensions for floor plan
  const galleryDimensions = { width: 1200, height: 800 }; // inches

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoadingProgress(0);
        setLoadingStages({
          fetching: true,
          processing: true,
          rendering: true
        });

        const urlCollectionId = new URLSearchParams(window.location.search).get('collection_id');
        if (!urlCollectionId) {
          setError('No collection ID provided');
          setIsLoading(false);
          return;
        }
        
        setCollectionId(urlCollectionId);
        
        // Get gallery_id from collection mapping
        try {
          const { data: collectionData, error: mappingError } = await supabase
            .from('gallery_collection_mapping')
            .select('gallery_id')
            .eq('collection_id', urlCollectionId)
            .single();
            
          if (mappingError && mappingError.code !== 'PGRST116') {
            console.warn('Could not find gallery mapping:', mappingError);
          }
          
          if (collectionData) {
            setGalleryId(collectionData.gallery_id);
            console.log(`🎯 Mapped collection ${urlCollectionId} to gallery ${collectionData.gallery_id}`);
          } else {
            // Fallback: use collection ID as gallery ID
            setGalleryId(urlCollectionId);
            console.log(`⚠️ No gallery mapping found, using collection ID as gallery ID: ${urlCollectionId}`);
          }
        } catch (mappingErr) {
          console.warn('Error fetching gallery mapping:', mappingErr);
          // Fallback: use collection ID as gallery ID
          setGalleryId(urlCollectionId);
        }

        // Stage 1: Fetching data
        setLoadingProgress(20);
        console.log(`🎯 Fetching scene elements for collection ${urlCollectionId}`);
        
        // Use enhanced fetch function
        const data = await fetchSceneElementsWithWalls(urlCollectionId, viewMode);
        setLoadingStages(prev => ({ ...prev, fetching: false }));
        setLoadingProgress(50);
        
        // Stage 2: Processing data
        console.log('Loaded scene data:', data);
        
        // NEW: Handle structured response with walls
        let processedData;
        if (data.walls && Array.isArray(data.walls)) {
          // New structured format
          processedData = {
            walls: data.walls || [],
            artworks: data.artworks || [],
            furniture: data.furniture || [],
            boxes: data.boxes || [],
            easels: data.easels || []
          };
        } else {
          // Fallback: organize flat array by type
          const organizedData = {
            walls: data.filter(el => el.type === 'wall_2d') || [],
            artworks: data.filter(el => el.type === 'artwork') || [],
            furniture: data.filter(el => el.type === 'furniture') || [],
            boxes: data.filter(el => el.type === 'box') || [],
            easels: data.filter(el => el.type === 'easel') || [],
            structures: data.filter(el => el.type === 'structure') || []
          };
          processedData = organizedData;
        }

        setLoadingStages(prev => ({ ...prev, processing: false }));
        setLoadingProgress(80);

        console.log(`✅ Loaded ${processedData.artworks.length} artworks`);
        console.log('📋 Sample artwork:', processedData.artworks[0]);
        
        // Debug: Check data structure for element_id
        if (processedData.artworks.length > 0) {
          const sampleArtwork = processedData.artworks[0];
          console.log('🔍 Sample artwork element_id:', sampleArtwork.element_id);
          console.log('🔍 Sample artwork vc_id:', sampleArtwork.vc_id);
          console.log('🔍 Sample artwork keys:', Object.keys(sampleArtwork));
        }
        
        if (processedData.furniture.length > 0) {
          const sampleFurniture = processedData.furniture[0];
          console.log('🔍 Sample furniture element_id:', sampleFurniture.element_id);
          console.log('🔍 Sample furniture keys:', Object.keys(sampleFurniture));
        }
        
        setSceneData(processedData);
        setLoadingStages(prev => ({ ...prev, rendering: false }));
        setLoadingProgress(100);

        // Fetch floor data
        try {
          console.log('🏠 Loading floor data for collection:', urlCollectionId);
          const floorData = await getFloorData(urlCollectionId);
          console.log('🏠 Raw floor data received:', floorData);
          setFloorData(floorData);
          console.log('✅ Floor data loaded and set in state:', floorData);
        } catch (error) {
          console.error('❌ Error loading floor data:', error);
          console.log('🏠 Setting default floor data');
          setFloorData({
            width: 2000,
            depth: 2000,
            position_x: 0,
            position_y: -0.5,
            position_z: 0,
            color: '#8B4513'
          });
        }

        // Fetch scene settings (background, lighting)
        try {
          console.log('🎨 Loading scene settings for collection:', urlCollectionId);
          const settings = await getSceneSettings(urlCollectionId);
          if (settings) {
            console.log('🎨 Scene settings loaded:', settings);
            setSceneSettings(settings);
          } else {
            console.log('🎨 No scene settings found, using defaults');
          }
        } catch (error) {
          console.error('❌ Error loading scene settings:', error);
        }

        // Small delay to show completion
        setTimeout(() => {
          setIsLoading(false);
        }, 500);
      } catch (error) {
        console.error('Error fetching scene data:', error);
        setError(error.message);
        setIsLoading(false);
      }
    };

    fetchData();
  }, [viewMode]);

  // Debug furniture data logging
  useEffect(() => {
    if (sceneData.furniture && sceneData.furniture.length > 0) {
      console.log('🪑 Furniture debug data:', sceneData.furniture.map(f => ({
        id: f.element_id,
        position: { x: f.position_x, y: f.position_y, z: f.position_z },
        scale: { x: f.scale_x, y: f.scale_y, z: f.scale_z },
        size: { w: f.width, h: f.height, d: f.depth },
        actualSize: {
          w: (parseFloat(f.width) || 24) * (parseFloat(f.scale_x) || 1) / 12,
          h: (parseFloat(f.height) || 24) * (parseFloat(f.scale_y) || 1) / 12,
          d: (parseFloat(f.depth) || 24) * (parseFloat(f.scale_z) || 1) / 12
        }
      })));
    }
  }, [sceneData.furniture]);

  /**
   * Calculate optimal initial camera position based on walls
   * Places camera at 5ft eye height, looking at the first wall or center of walls
   */
  const calculateOptimalCameraPosition = () => {
    const walls = sceneData.walls || [];
    
    if (walls.length === 0) {
      // No walls - return default overview position
      return { position: '0 5 15', rotation: '0 0 0' };
    }
    
    // Use the first wall as the primary viewing target
    const firstWall = walls[0];
    const wallX = (parseFloat(firstWall.wall_center_x) || 0) / 12; // Convert inches to feet
    const wallZ = (parseFloat(firstWall.wall_center_z) || 0) / 12;
    const wallRotY = parseFloat(firstWall.wall_rotation_y) || 0;
    const wallWidth = (parseFloat(firstWall.width) || 120) / 12;
    const wallHeight = (parseFloat(firstWall.height) || 96) / 12;
    
    // Calculate viewing distance based on wall size
    // Rule of thumb: stand back 1.5x the wall width or 2x the height, whichever is larger
    const optimalDistance = Math.max(wallWidth * 1.5, wallHeight * 2, 10); // Minimum 10 feet
    
    // Calculate camera position based on wall rotation
    // Wall rotation is around Y axis - camera should be in front of the wall
    const wallRotRad = (wallRotY * Math.PI) / 180;
    
    // Wall faces perpendicular to its rotation - camera should be on the normal side
    // If wall rotation is 0, it faces +Z, so camera should be at higher Z
    const cameraX = wallX - Math.sin(wallRotRad) * optimalDistance;
    const cameraZ = wallZ + Math.cos(wallRotRad) * optimalDistance;
    
    // Camera at 5 feet eye height
    const cameraY = 5;
    
    // Camera rotation: look at the wall
    // Add 180 to look back at the wall
    const cameraRotY = wallRotY + 180;
    
    // Calculate vertical tilt to look at wall center
    const wallCenterY = (parseFloat(firstWall.wall_center_y) || 48) / 12; // Default 4 feet up
    const verticalAngle = Math.atan2(wallCenterY - cameraY, optimalDistance) * (180 / Math.PI);
    
    console.log('📷 Optimal camera calculation:', {
      firstWall: firstWall.element_id,
      wallPos: `(${wallX.toFixed(1)}, ${wallZ.toFixed(1)})`,
      wallRot: wallRotY,
      wallSize: `${wallWidth.toFixed(1)}' x ${wallHeight.toFixed(1)}'`,
      optimalDistance: optimalDistance.toFixed(1),
      cameraPos: `(${cameraX.toFixed(1)}, ${cameraY}, ${cameraZ.toFixed(1)})`,
      cameraRot: `(${verticalAngle.toFixed(1)}, ${cameraRotY}, 0)`
    });
    
    return {
      position: `${cameraX.toFixed(2)} ${cameraY} ${cameraZ.toFixed(2)}`,
      rotation: `${verticalAngle.toFixed(1)} ${cameraRotY} 0`
    };
  };

  // Initialize camera when A-Frame scene loads
  useEffect(() => {
    if (currentView !== '3d') return;

    const handleSceneLoaded = () => {
      console.log('🎬 A-Frame scene loaded, camera should be ready');
      const camera = document.querySelector('#camera');
      if (camera) {
        console.log('📷 Camera found and ready:', camera);
        // Calculate optimal camera position based on walls
        const { position, rotation } = calculateOptimalCameraPosition();
        console.log('📷 Setting initial camera to:', position, rotation);
        moveCamera(position, rotation);
      } else {
        console.warn('⚠️ Camera not found on scene load');
      }
    };

    // Listen for A-Frame scene loaded event
    document.addEventListener('aframe-loaded', handleSceneLoaded);
    
    // Also check after a delay
    setTimeout(handleSceneLoaded, 2000);

    return () => {
      document.removeEventListener('aframe-loaded', handleSceneLoaded);
    };
  }, [currentView]);

  // Keyboard shortcuts (only active in 3D view)
  useEffect(() => {
    if (currentView !== '3d') return;

    const handleKeyPress = (e) => {
      if (!selectedElement) return;

      // Only handle movement keys if we have a selected element
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'PageUp', 'PageDown', 'Delete', 'Escape'].includes(e.key)) {
        e.preventDefault();
      }

      // Fine-tuning with Shift key: smaller increments for precise control
      const moveDistance = e.shiftKey ? 2 : 12;  // 2 inches for fine, 12 inches for normal
      const rotateAmount = e.shiftKey ? 2 : 15;  // 2 degrees for fine, 15 degrees for normal

      console.log('⌨️ Key pressed:', e.key, 'Selected element:', selectedElement, 'Type:', selectedElement.type);

      switch (e.key) {
        case 'ArrowUp':
          console.log('⌨️ Moving element up by', -moveDistance);
          moveElement(0, 0, -moveDistance);
          break;
        case 'ArrowDown':
          console.log('⌨️ Moving element down by', moveDistance);
          moveElement(0, 0, moveDistance);
          break;
        case 'ArrowLeft':
          if (e.ctrlKey || e.metaKey) {
            console.log('⌨️ Rotating element left by', -rotateAmount);
            rotateElement(0, -rotateAmount, 0);
          } else {
            console.log('⌨️ Moving element left by', -moveDistance);
            moveElement(-moveDistance, 0, 0);
          }
          break;
        case 'ArrowRight':
          if (e.ctrlKey || e.metaKey) {
            console.log('⌨️ Rotating element right by', rotateAmount);
            rotateElement(0, rotateAmount, 0);
          } else {
            console.log('⌨️ Moving element right by', moveDistance);
            moveElement(moveDistance, 0, 0);
          }
          break;
        case 'PageUp':
          console.log('⌨️ Moving element up (Y) by', moveDistance);
          moveElement(0, moveDistance, 0);
          break;
        case 'PageDown':
          console.log('⌨️ Moving element down (Y) by', -moveDistance);
          moveElement(0, -moveDistance, 0);
          break;
        case 'Delete':
          deleteElement();
          break;
        case 'Escape':
          setSelectedElement(null);
          setSelectedElements([]);
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [selectedElement, currentView]);

  useEffect(() => {
    const handleHelpToggle = (e) => {
      if (e.key === '?' || e.key === 'h') {
        setShowHelp(prev => !prev);
      }
    };

    window.addEventListener('keydown', handleHelpToggle);
    return () => window.removeEventListener('keydown', handleHelpToggle);
  }, [showHelp]);

  // Update camera controls when select mode changes
  useEffect(() => {
    const updateCameraControls = () => {
      // Try multiple camera selectors
      let camera = document.querySelector('#camera');
      if (!camera) {
        camera = document.querySelector('[camera]');
      }
      if (!camera) {
        camera = document.querySelector('a-camera');
      }
      
      if (camera) {
        console.log('🎥 Updating camera controls for select mode:', isSelectMode);
        if (isSelectMode) {
          camera.setAttribute('look-controls', 'enabled: false');
          camera.setAttribute('wasd-controls', 'enabled: false');
        } else {
          camera.setAttribute('look-controls', 'enabled: true');
          camera.setAttribute('wasd-controls', 'enabled: true');
        }
      }
    };

    // Update immediately and also after a short delay to ensure camera exists
    updateCameraControls();
    setTimeout(updateCameraControls, 100);
  }, [isSelectMode]);

  // Handler for selecting an element from the list
  const handleSelectElement = (element, multiSelect = false, shouldMoveCamera = true) => {
    console.log('🎯 Selected element:', element);
    console.log('🎯 Element type:', element.type);
    console.log('🎯 Element ID:', element.element_id);
    console.log('🎯 Element VC ID:', element.vc_id);
    console.log('🎯 Move camera:', shouldMoveCamera);
    
    if (multiSelect) {
      if (!selectedElements.find(el => el.element_id === element.element_id)) {
        setSelectedElements([...selectedElements, element]);
      }
    } else {
      setSelectedElement(element);
      setSelectedElements([element]);
      // Set select mode to true when an element is selected
      setIsSelectMode(true);
      console.log('✅ Element selected, ControlPanel should appear, and select mode enabled');
      
      // For walls, never move the camera
      if (element.type === 'wall_2d') {
        console.log('🏛️ Wall selected - camera will not move');
      } else if (shouldMoveCamera) {
        // Center camera on selected element (only for non-wall elements)
        const posX = (element.position_x / 12) || 0;
        const posY = (element.position_y / 12) || 0;
        const posZ = (element.position_z / 12) || 0;
        
        // Position camera slightly above and in front of the element
        const cameraX = posX;
        const cameraY = posY + 3; // 3 feet above
        const cameraZ = posZ + 8; // 8 feet in front
        
        console.log(`📷 Moving camera to: ${cameraX} ${cameraY} ${cameraZ}`);
        moveCamera(`${cameraX} ${cameraY} ${cameraZ}`, '0 0 0');
      }
    }
  };

  // FIX: Enhanced element update to sync coordinates properly
  const handleUpdateElement = async (updated) => {
    try {

      
      // Check if this is a close signal
      if (updated._clearSelection) {
        console.log('❌ Clearing selection');
        setSelectedElement(null);
        setSelectedElements([]);
        return;
      }
      
      // Handle both position object format (from ControlPanel) and direct properties
      let position_x, position_y, position_z, rotation_x, rotation_y, rotation_z;
      
      if (updated.position) {
        // ControlPanel sends position as object with x, y, z
        position_x = parseFloat(updated.position.x) || 0;
        position_y = parseFloat(updated.position.y) || 0;
        position_z = parseFloat(updated.position.z) || 0;
      } else {
        // Direct property format
        position_x = parseFloat(updated.position_x) || 0;
        position_y = parseFloat(updated.position_y) || 0;
        position_z = parseFloat(updated.position_z) || 0;
      }
      
      // Priority: direct rotation_x/y/z properties first, then rotation object
      if (updated.rotation_x !== undefined || updated.rotation_y !== undefined || updated.rotation_z !== undefined) {
        // Direct property format (what ControlPanel sends)
        rotation_x = parseFloat(updated.rotation_x) || 0;
        rotation_y = parseFloat(updated.rotation_y) || 0;
        rotation_z = parseFloat(updated.rotation_z) || 0;
      } else if (updated.rotation) {
        // ControlPanel sends rotation as object with x, y, z
        rotation_x = parseFloat(updated.rotation.x) || 0;
        rotation_y = parseFloat(updated.rotation.y) || 0;
        rotation_z = parseFloat(updated.rotation.z) || 0;
      } else {
        // Fallback to 0
        rotation_x = 0;
        rotation_y = 0;
        rotation_z = 0;
      }
      
      // Convert coordinates to match database format
      // Floor Plan works in inches, 3D works in feet
      const updatedForDatabase = {
        ...updated,
        // Ensure positions are stored as database expects (inches)
        position_x,
        position_y,
        position_z,
        rotation_x,
        rotation_y,
        rotation_z
      };



      // Update local state
      const updateElementInArray = (arr) =>
        arr.map(el => {
          const isMatch = el.element_id === updated.element_id || 
                         (el.vc_id && updated.vc_id && el.vc_id === updated.vc_id);
          return isMatch ? { ...el, ...updatedForDatabase } : el;
        });
      
      setSceneData(prev => {
        const newState = { ...prev };
        if (updated.type === 'wall_2d') {
          newState.walls = updateElementInArray(prev.walls);
          
          // If wall was moved or rotated, sync artwork positions
          if (updated.wall_center_x !== undefined || updated.wall_center_y !== undefined || updated.wall_center_z !== undefined || updated.wall_rotation_y !== undefined) {
            console.log('🏛️ Wall moved or rotated, syncing artwork positions...');
            // Trigger artwork sync after a short delay to ensure wall update is complete
            setTimeout(async () => {
              try {
                await syncArtworkPositionsOnWall(updated.element_id);
                console.log('✅ Artwork positions synced after wall move/rotation');
              } catch (error) {
                console.error('❌ Error syncing artwork positions:', error);
              }
            }, 100);
          }
        } else if (updated.type === 'artwork') {
          newState.artworks = updateElementInArray(prev.artworks);
        } else if (updated.type === 'box') {
          newState.boxes = updateElementInArray(prev.boxes);
        } else if (updated.type === 'easel') {
          newState.easels = updateElementInArray(prev.easels);
        } else if (updated.type === 'furniture') {
          newState.furniture = updateElementInArray(prev.furniture);
        }
        return newState;
      });
      
      setSelectedElement(updatedForDatabase);
      

    } catch (error) {
      console.error('❌ Error updating element:', error);
    }
  };

  const moveElement = async (deltaX, deltaY, deltaZ) => {
    if (!selectedElement) return;

    console.log('🎯 moveElement called:', { deltaX, deltaY, deltaZ, selectedElement });

    // Get the current element from scene data to ensure we have the latest coordinates
    const getCurrentElement = () => {
      const allElements = [
        ...sceneData.walls,  // Include walls for movement
        ...sceneData.artworks,
        ...sceneData.boxes,
        ...sceneData.easels,
        ...sceneData.furniture
      ];
      
      return allElements.find(el => 
        (selectedElement.type === 'artwork' && el.vc_id === selectedElement.vc_id) ||
        el.element_id === selectedElement.element_id
      ) || selectedElement;
    };

    const currentElement = getCurrentElement();
    console.log('🎯 Current element from scene data:', currentElement);

    // Handle walls differently - they use wall_center_* fields
    if (currentElement.type === 'wall_2d') {
      const newElement = {
        ...currentElement,
        wall_center_x: (parseFloat(currentElement.wall_center_x) || 0) + deltaX,
        wall_center_y: (parseFloat(currentElement.wall_center_y) || 0) + deltaY,
        wall_center_z: (parseFloat(currentElement.wall_center_z) || 0) + deltaZ,
      };
      
      console.log('🎯 New wall position:', { 
        wall_center_x: newElement.wall_center_x, 
        wall_center_y: newElement.wall_center_y, 
        wall_center_z: newElement.wall_center_z 
      });
      
      try {
        await handleUpdateElement(newElement);
        console.log(`✅ Moved wall by (${deltaX}, ${deltaY}, ${deltaZ})`);
      } catch (error) {
        console.error('❌ Error moving wall:', error);
      }
      return;
    }
    
    // Handle other elements normally
    const newElement = {
      ...currentElement,
      position_x: (parseFloat(currentElement.position_x) || 0) + deltaX,
      position_y: (parseFloat(currentElement.position_y) || 0) + deltaY,
      position_z: (parseFloat(currentElement.position_z) || 0) + deltaZ,
    };

    console.log('🎯 New element position:', { 
      position_x: newElement.position_x, 
      position_y: newElement.position_y, 
      position_z: newElement.position_z 
    });

    try {
      await handleUpdateElement(newElement);
      console.log(`✅ Moved element by (${deltaX}, ${deltaY}, ${deltaZ})`);
    } catch (error) {
      console.error('❌ Error moving element:', error);
    }
  };

  const rotateElement = async (deltaX, deltaY, deltaZ) => {
    if (!selectedElement) return;

    // Handle walls differently - they use wall_rotation_y
    if (selectedElement.type === 'wall_2d') {
      const newElement = {
        ...selectedElement,
        wall_rotation_y: ((selectedElement.wall_rotation_y || 0) + deltaY) % 360,
      };

      console.log(`🔄 Rotating wall ${selectedElement.element_id}:`, {
        currentRotation: {
          y: selectedElement.wall_rotation_y || 0
        },
        delta: { y: deltaY },
        newRotation: {
          y: newElement.wall_rotation_y
        },
        position: {
          x: selectedElement.wall_center_x,
          y: selectedElement.wall_center_y,
          z: selectedElement.wall_center_z
        }
      });

      try {
        await handleUpdateElement(newElement);
        console.log(`✅ Rotated wall by Y: ${deltaY}`);
      } catch (error) {
        console.error('❌ Error rotating wall:', error);
      }
      return;
    }

    // Handle other elements normally
    const newElement = {
      ...selectedElement,
      rotation_x: ((selectedElement.rotation_x || 0) + deltaX) % 360,
      rotation_y: ((selectedElement.rotation_y || 0) + deltaY) % 360,
      rotation_z: ((selectedElement.rotation_z || 0) + deltaZ) % 360,
    };

    console.log(`🔄 Rotating element ${selectedElement.element_id}:`, {
      currentRotation: {
        x: selectedElement.rotation_x || 0,
        y: selectedElement.rotation_y || 0,
        z: selectedElement.rotation_z || 0
      },
      delta: { x: deltaX, y: deltaY, z: deltaZ },
      newRotation: {
        x: newElement.rotation_x,
        y: newElement.rotation_y,
        z: newElement.rotation_z
      },
      position: {
        x: selectedElement.position_x,
        y: selectedElement.position_y,
        z: selectedElement.position_z
      }
    });

    try {
      await handleUpdateElement(newElement);
      console.log(`✅ Rotated element by (${deltaX}, ${deltaY}, ${deltaZ})`);
    } catch (error) {
      console.error('❌ Error rotating element:', error);
    }
  };

  const deleteElement = async () => {
    if (!selectedElement || !confirm('Delete this item?')) return;

    try {
      // await deleteSceneElement(selectedElement.element_id);
      setSelectedElement(null);
      console.log('Delete element (not implemented yet)');
    } catch (error) {
      console.error('Error deleting element:', error);
    }
  };

  const cameraPresets = [
    { name: 'Front', position: '0 5 20', rotation: '0 0 0', icon: '👁️' },
    { name: 'Curator', position: '0 12 15', rotation: '-30 0 0', icon: '🎨' },
    { name: 'Overview', position: '0 25 10', rotation: '-60 0 0', icon: '🗺️' },
    { name: 'Top Down', position: '0 50 0', rotation: '-90 0 0', icon: '📐' },
    { name: 'Detail', position: '0 3 8', rotation: '0 0 0', icon: '🔍' },
    { name: 'Side', position: '20 5 0', rotation: '0 90 0', icon: '👈' }
  ];

  // FIX: Camera movement function (replace your existing moveCamera function)
  const moveCamera = (position, rotation) => {
    // Try multiple camera selectors
    let camera = document.querySelector('#camera');
    if (!camera) {
      camera = document.querySelector('[camera]');
    }
    if (!camera) {
      camera = document.querySelector('a-camera');
    }
    
    console.log('🎥 moveCamera called:', position, '|', rotation);
    console.log('🎥 camera element found:', !!camera);
    
    if (camera) {
      // Check if this is the top-down view
      const isTopDown = position.includes('50') && rotation.includes('-90');
      setIsTopDownView(isTopDown);
      
      console.log('🎥 Setting camera animation to:', position, '|', rotation);
      
      // Remove existing animations first
      camera.removeAttribute('animation__position');
      camera.removeAttribute('animation__rotation');
      
      // Set new animations
      camera.setAttribute('animation__position', {
        property: 'position',
        to: position,
        dur: 1000,
        easing: 'easeInOutQuad'
      });
      
      console.log('🎥 Setting rotation animation to:', rotation);
      camera.setAttribute('animation__rotation', {
        property: 'rotation',
        to: rotation,
        dur: 1000,
        easing: 'easeInOutQuad'
      });

      // Simple approach: just set rotation directly
      console.log('🎥 Setting camera rotation to:', rotation);
      camera.setAttribute('rotation', rotation);

      // IMPORTANT: A-Frame's look-controls component overrides the camera
      // rotation every tick from its internal pitch/yaw objects. Setting
      // rotation directly (or via animation) is silently reverted unless we
      // also update those internals. This is why presets like "Overview"
      // and "Top Down" appeared to do nothing.
      try {
        const [rx = 0, ry = 0] = rotation.split(/\s+/).map(Number);
        const toRad = (d) => (d * Math.PI) / 180;
        const applyLookControls = () => {
          const lc = camera.components && camera.components['look-controls'];
          if (lc) {
            if (lc.pitchObject) lc.pitchObject.rotation.x = toRad(rx);
            if (lc.yawObject) lc.yawObject.rotation.y = toRad(ry);
          }
        };
        // Apply immediately and again after the animation finishes so the
        // final rotation sticks regardless of timing.
        applyLookControls();
        setTimeout(applyLookControls, 1050);
      } catch (e) {
        console.warn('🎥 Failed to sync look-controls rotation:', e);
      }
    } else {
      console.log('❌ Camera not found! Trying to create default camera...');
      // Create a default camera if none exists
      const scene = document.querySelector('a-scene');
      if (scene) {
        const defaultCamera = document.createElement('a-camera');
        defaultCamera.setAttribute('id', 'camera');
        defaultCamera.setAttribute('position', '0 5 20');
        // Ensure camera controls are disabled when in select mode
        if (isSelectMode) {
          defaultCamera.setAttribute('look-controls', 'enabled: false');
          defaultCamera.setAttribute('wasd-controls', 'enabled: false');
        }
        scene.appendChild(defaultCamera);
        console.log('🎥 Created default camera with controls:', isSelectMode ? 'disabled' : 'enabled');
        // Try the move again
        setTimeout(() => moveCamera(position, rotation), 100);
      }
    }
  };

  // NEW: View switcher functions
  const switchView = (newView) => {
    setCurrentView(newView);
    if (newView === '3d') {
      // Reset camera to optimal position based on walls when switching to 3D
      setTimeout(() => {
        const { position, rotation } = calculateOptimalCameraPosition();
        moveCamera(position, rotation);
      }, 100);
    }
  };

  // Bulk-sync all artwork 3D positions from their wall placements
  const handleSyncAllArtwork = async () => {
    if (isSyncing) return;
    setIsSyncing(true);
    try {
      const walls = sceneData.walls || [];
      let totalSynced = 0;
      for (const wall of walls) {
        try {
          const result = await syncArtworkPositionsOnWall(wall.element_id);
          totalSynced += result.count || 0;
        } catch (err) {
          console.warn(`⚠️ Sync failed for wall ${wall.element_id}:`, err);
        }
      }
      console.log(`✅ Bulk sync complete: ${totalSynced} artworks updated across ${walls.length} walls`);
      // Refresh scene data
      const data = await fetchSceneElementsWithWalls(collectionId, viewMode);
      if (data.walls && Array.isArray(data.walls)) {
        setSceneData({
          walls: data.walls || [],
          artworks: data.artworks || [],
          furniture: data.furniture || [],
          boxes: data.boxes || [],
          easels: data.easels || [],
          structures: data.structures || []
        });
      }
      alert(`Synced ${totalSynced} artworks across ${walls.length} walls.`);
    } catch (err) {
      console.error('❌ Bulk sync error:', err);
      alert('Sync failed — see console for details.');
    } finally {
      setIsSyncing(false);
    }
  };

  // Make moveCamera available globally for ListPanel to use
  React.useEffect(() => {
    window.moveCamera = moveCamera;
    return () => {
      delete window.moveCamera;
    };
  }, []);

  /**
   * Handle wall movement in 3D space
   */
  const handleWallMoved = async (wallId, newPosition, newRotation) => {
    try {
      console.log(`🏛️ Moving wall ${wallId} to:`, newPosition, newRotation);
      
      await updateWallPosition(wallId, {
        wall_center_x: newPosition.x * 12, // Convert feet to inches
        wall_center_y: newPosition.y * 12,
        wall_center_z: newPosition.z * 12,
        wall_rotation_y: newRotation.y
      });
      
      await syncArtworkPositionsOnWall(wallId);
      await fetchData(); // Refresh data
      
      console.log(`✅ Wall ${wallId} moved successfully`);
      
    } catch (error) {
      console.error(`❌ Error moving wall ${wallId}:`, error);
    }
  };

  /**
   * Render 3D walls with artwork textures
   */
  const render3DWalls = () => {
    const walls = sceneData.walls || [];
    
    if (walls.length === 0) {
      console.log('No walls found in scene data');
      return null;
    }
    
    // Group walls by pod_group so we can align multi-wall pods
    const podGroupMap = {};
    walls.forEach(w => {
      try {
        const meta = w.text_content ? JSON.parse(w.text_content) : null;
        if (meta?.pod_group) {
          if (!podGroupMap[meta.pod_group]) podGroupMap[meta.pod_group] = [];
          podGroupMap[meta.pod_group].push(w);
        }
      } catch { /* ignore */ }
    });

    // For multi-wall pods, align all walls to the primary's rotation and
    // arrange them side-by-side along the wall's local X axis.
    // This fixes legacy L-shape pods where walls were 90° apart.
    const wallOverrides = new Map();
    Object.values(podGroupMap).forEach(group => {
      if (group.length < 2) return;

      // Primary = widest wall (or first if tied)
      const primary = group.reduce((best, w) =>
        (parseFloat(w.width) || 0) >= (parseFloat(best.width) || 0) ? w : best
      , group[0]);

      const rotY = parseFloat(primary.wall_rotation_y) || 0;
      const rotYRad = rotY * Math.PI / 180;
      const primaryCx = parseFloat(primary.wall_center_x) || 0;
      const primaryCz = parseFloat(primary.wall_center_z) || 0;

      // Ensure tent-panel metadata
      let tiltAngle = 70;
      try { tiltAngle = JSON.parse(primary.text_content)?.tilt_angle || 70; } catch {}
      const tentMeta = JSON.stringify({
        pod_group: JSON.parse(primary.text_content).pod_group,
        template: 'tent-panel',
        tilt_angle: tiltAngle,
        wallWidth: parseFloat(primary.width),
        wallHeight: parseFloat(primary.height)
      });

      // Place secondary walls next to the primary along local-X
      const others = group.filter(w => w.element_id !== primary.element_id);
      others.forEach((w, i) => {
        const offset = parseFloat(primary.width) * (i + 1); // inches along local X
        // local X at rotation rotY: world dx = offset * cos(rotY), dz = -offset * sin(rotY)
        // (A-Frame: rotY=0 means wall faces +Z, local X = world +X)
        const cosR = Math.cos(rotYRad);
        const sinR = Math.sin(rotYRad);
        wallOverrides.set(w.element_id, {
          wall_center_x: primaryCx - offset * cosR,
          wall_center_z: primaryCz + offset * sinR,
          wall_center_y: primary.wall_center_y,
          wall_rotation_y: rotY,
          width: w.width,
          height: w.height,
          text_content: tentMeta
        });
      });

      // Also ensure primary has tent-panel metadata
      wallOverrides.set(primary.element_id, {
        text_content: tentMeta
      });
    });

    // Apply overrides
    const correctedWalls = walls.map(w => {
      const overrides = wallOverrides.get(w.element_id);
      return overrides ? { ...w, ...overrides } : w;
    });

    console.log(`🏛️ Rendering ${correctedWalls.length} 3D walls (${wallOverrides.size} pod walls corrected)`);
    
    const processedWalls = positionUnplacedWalls(correctedWalls);
    
    // Group artworks by wall
    const artworksByWall = {};
    (sceneData.artworks || []).forEach(artwork => {
      if (artwork.parent_element_id) {
        if (!artworksByWall[artwork.parent_element_id]) {
          artworksByWall[artwork.parent_element_id] = [];
        }
        artworksByWall[artwork.parent_element_id].push(artwork);
      }
    });
    
    return processedWalls.map(wall => (
      <Wall3DBox
        key={wall.element_id}
        wall={wall}
        artworks={artworksByWall[wall.element_id] || []}
        selectedElement={selectedElement}
        onSelectElement={setSelectedElement}
        onWallMoved={(newPos, newRot) => handleWallMoved(wall.element_id, newPos, newRot)}
        isMovable={true}
        getTextureUrl={get3DTextureUrl}
      />
    ));
  };

  /**
   * Render easels with artwork in 3D
   * Simplified: ground marker + artwork floating at centerline height
   * The easel's position_y controls the artwork centerline (like adjusting a real easel rail).
   * Defaults to 60" (5ft eye level) when position_y is 0.
   */
  const renderEaselsWithArtwork = () => {
    const easels = sceneData.easels || [];
    if (easels.length === 0) return null;

    // Group artworks by parent easel
    const artworksByEasel = {};
    (sceneData.artworks || []).forEach(artwork => {
      if (artwork.parent_element_id) {
        const parentEasel = easels.find(e => e.element_id === artwork.parent_element_id);
        if (parentEasel) {
          artworksByEasel[artwork.parent_element_id] = artwork;
        }
      }
    });

    return easels.map((easel) => {
      const isSelected = selectedElement && selectedElement.element_id === easel.element_id;

      // Floor position (inches to feet)
      const posX = (parseFloat(easel.position_x) || 0) / 12;
      const posZ = (parseFloat(easel.position_z) || 0) / 12;
      const rotY = parseFloat(easel.rotation_y) || 0;

      // Centerline height: position_y controls it, default 60" (5ft) eye level
      const centerlineInches = parseFloat(easel.position_y) || 60;
      const centerY = centerlineInches / 12; // feet

      // Artwork on this easel
      const artwork = artworksByEasel[easel.element_id];
      const artW = artwork ? (parseFloat(artwork.width) || 24) / 12 : (parseFloat(easel.width) || 24) / 12;
      const artH = artwork ? (parseFloat(artwork.height) || 24) / 12 : (parseFloat(easel.height) || 36) / 12;

      return (
        <a-entity
          key={`easel-${easel.element_id}`}
          position={`${posX} 0 ${posZ}`}
          rotation={`0 ${rotY} 0`}
        >
          {/* Ground marker — amber disc on floor */}
          <a-circle
            radius="0.5"
            position="0 0.02 0"
            rotation="-90 0 0"
            material="color: #d97706; opacity: 0.6; transparent: true"
          />
          {/* Easel label on ground */}
          <a-text
            value={easel.source || 'Easel'}
            position="0 0.03 0"
            rotation="-90 0 0"
            align="center"
            color="#92400e"
            scale="1.2 1.2 1.2"
          />
          {/* Thin vertical post from floor to artwork center */}
          <a-cylinder
            radius="0.02"
            height={centerY}
            position={`0 ${centerY / 2} 0`}
            material="color: #d97706; opacity: 0.4; transparent: true"
          />

          {/* Artwork floating at centerline (or placeholder if no artwork) */}
          {artwork && artwork.source ? (
            <>
              {/* Thin black frame border — behind the artwork */}
              <a-box
                width={artW + 0.08}
                height={artH + 0.08}
                depth="0.01"
                position={`0 ${centerY} 0`}
                material="color: #000000; roughness: 0.7"
              />
              {/* Artwork image at natural dimensions — in front of frame */}
              <a-plane
                width={artW}
                height={artH}
                position={`0 ${centerY} 0.05`}
                material={artwork.source ? `shader: flat; src: ${get3DTextureUrl(artwork.source)}` : 'color: #374151; opacity: 0.6; transparent: true'}
                className="clickable"
                onClick={() => setSelectedElement(artwork)}
              />
            </>
          ) : (
            /* Empty easel placeholder */
            <a-plane
              width="1.5"
              height="1.5"
              position={`0 ${centerY} 0.01`}
              material="color: #d97706; opacity: 0.15; transparent: true"
              className="clickable"
              onClick={() => setSelectedElement(easel)}
            />
          )}

          {/* Clickable hitbox for selecting the easel */}
          <a-box
            width="1"
            height={centerY + artH / 2 + 0.5}
            depth="0.5"
            position={`0 ${(centerY + artH / 2 + 0.5) / 2} 0`}
            material="color: red; opacity: 0.0; transparent: true"
            className="clickable movable"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedElement(easel);
            }}
          />

          {/* Selection indicator */}
          {isSelected && (
            <>
              <a-circle
                radius="0.6"
                position="0 0.03 0"
                rotation="-90 0 0"
                material="color: #fbbf24; opacity: 0.8; transparent: true"
                animation="property: scale; from: 1 1 1; to: 1.1 1.1 1.1; dur: 800; dir: alternate; loop: true"
              />
              <a-text
                value={`${easel.source || 'Easel'} (${centerlineInches}")`}
                position={`0 ${centerY + artH / 2 + 0.3} -0.02`}
                align="center"
                color="yellow"
                scale="1.2 1.2 1.2"
              />
            </>
          )}
        </a-entity>
      );
    });
  };

  /**
   * Render structural elements (columns, solid walls) in 3D
   * These are non-artwork-bearing objects.
   */
  const renderStructures = () => {
    const structureList = sceneData.structures || [];
    if (structureList.length === 0) return null;

    return structureList.map((s) => {
      const posX = (parseFloat(s.position_x) || 0) / 12;
      const posZ = (parseFloat(s.position_z) || 0) / 12;
      const rotY = parseFloat(s.rotation_y) || 0;
      const w = (parseFloat(s.width) || 12) / 12;
      const h = (parseFloat(s.height) || 96) / 12;
      const d = (parseFloat(s.depth) || 12) / 12;
      const color = s.color || '#9ca3af';
      let meta;
      try { meta = s.text_content ? JSON.parse(s.text_content) : {}; } catch { meta = {}; }
      const shape = meta.shape || 'column-square';
      const isRound = shape === 'column-round';
      const sOpacity = meta.opacity ?? 1;
      const opacityMat = sOpacity < 1 ? `; opacity: ${sOpacity}; transparent: true` : '';
      const isSelected = selectedElement && selectedElement.element_id === s.element_id;

      return (
        <a-entity
          key={`structure-${s.element_id}`}
          position={`${posX} ${h / 2} ${posZ}`}
          rotation={`0 ${rotY} 0`}
        >
          {isRound ? (
            <a-cylinder
              radius={w / 2}
              height={h}
              material={`color: ${color}; roughness: 0.8${opacityMat}`}
              shadow="cast: true; receive: true"
              className="clickable"
              onClick={() => setSelectedElement(s)}
            />
          ) : (
            <a-box
              width={w}
              height={h}
              depth={d}
              material={`color: ${color}; roughness: 0.8${opacityMat}`}
              shadow="cast: true; receive: true"
              className="clickable"
              onClick={() => setSelectedElement(s)}
            />
          )}
          {isSelected && (
            <a-ring
              radius-inner={Math.max(w, d) * 0.7}
              radius-outer={Math.max(w, d) * 0.75}
              position={`0 ${-h / 2 + 0.05} 0`}
              rotation="-90 0 0"
              material="color: #fbbf24; opacity: 0.8; transparent: true"
              animation="property: rotation; from: -90 0 0; to: -90 0 360; dur: 2000; loop: true"
            />
          )}
        </a-entity>
      );
    });
  };

  /**
   * Render only unplaced artworks (not on walls or easels)
   */
  const renderUnplacedArtworks = () => {
    const unplacedArtworks = (sceneData.artworks || []).filter(artwork => 
      !artwork.parent_element_id
    );
    
    if (unplacedArtworks.length === 0) return null;
    
    console.log(`🎨 Rendering ${unplacedArtworks.length} unplaced artworks`);
    
    return unplacedArtworks.map((artwork) => {
      const isSelected = selectedElement && selectedElement.element_id === artwork.element_id;
      
      const posX = (parseFloat(artwork.position_x) || 0) / 12;
      const posY = (parseFloat(artwork.position_y) || 0) / 12;
      const posZ = (parseFloat(artwork.position_z) || 0.2) / 12; // Increased offset to prevent z-fighting and flashing
      
      const width = (parseFloat(artwork.width) || 24) / 12;
      const height = (parseFloat(artwork.height) || 24) / 12;
      
      return (
        <a-entity key={artwork.element_id} className="unplaced-artwork">
          <a-box
            width={width + 0.1}
            height={height + 0.1}
            depth="0.02"
            position={`${posX} ${posY} ${posZ - 0.03}`}
            material="color: #000000; roughness: 0.7"
            className="artwork-frame-unplaced"
          />
          
          <a-plane
            id={`artwork-unplaced-${artwork.element_id}`}
            width={width}
            height={height}
            position={`${posX} ${posY} ${posZ}`}
            material={artwork.source ? `shader: flat; src: ${get3DTextureUrl(artwork.source)}` : 'color: #374151; opacity: 0.6; transparent: true'}
            className={`artwork-unplaced ${isSelected ? 'selected' : ''}`}
            data-artwork-id={artwork.element_id}
            data-vc-id={artwork.vc_id}
            onClick={() => setSelectedElement(artwork)}
          />
          
          {isSelected && (
            <a-ring
              radius-inner={Math.max(width, height) * 0.6}
              radius-outer={Math.max(width, height) * 0.65}
              position={`${posX} ${posY} ${posZ + 0.05}`}
              material="color: #ffff00; opacity: 0.9"
              animation="property: rotation; to: 0 0 360; dur: 2000; loop: true"
              className="selection-ring-unplaced"
            />
          )}
        </a-entity>
      );
    });
  };

  /**
   * Debug function for troubleshooting wall data
   */
  const debugWallData = () => {
    console.log('=== WALL DEBUG INFO ===');
    console.log('Scene data walls:', sceneData.walls);
    
    sceneData.walls?.forEach(wall => {
      console.log(`Wall ${wall.element_id}:`, {
        source: wall.source,
        dimensions: `${wall.width}" × ${wall.height}"`,
        has3DPosition: !!(wall.wall_center_x && wall.wall_center_z),
        position3D: wall.wall_center_x ? 
          `(${wall.wall_center_x}, ${wall.wall_center_y}, ${wall.wall_center_z})` : 
          'Not positioned',
        artworkCount: sceneData.artworks?.filter(art => 
          art.parent_element_id === wall.element_id
        ).length || 0
      });
    });
  };

  // Call debugWallData() in useEffect for testing
  useEffect(() => {
    if (sceneData.walls && sceneData.walls.length > 0) {
      debugWallData();
    }
  }, [sceneData]);

  if (isLoading) {
    return (
      <div id="loading-overlay">
        <div className="loading-content">
          <div className="spinner-border loading-spinner" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <h4>Loading 3D Gallery...</h4>
          
          {/* Progress indicator */}
          <div className="loading-progress">
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `${loadingProgress}%` }}
              ></div>
            </div>
            <p className="progress-text">{loadingProgress}% Complete</p>
          </div>
          
          {/* Stage indicators */}
          <div className="loading-stages">
            <div className={`stage-item ${!loadingStages.fetching ? 'completed' : 'active'}`}>
              {!loadingStages.fetching ? <span>✓</span> : <span>⏳</span>}
              <span>Fetching Scene Data</span>
            </div>
            <div className={`stage-item ${!loadingStages.processing ? 'completed' : loadingStages.fetching ? 'pending' : 'active'}`}>
              {!loadingStages.processing ? <span>✓</span> : loadingStages.fetching ? <span>⏳</span> : <div className="mini-spinner"></div>}
              <span>Processing Elements</span>
            </div>
            <div className={`stage-item ${!loadingStages.rendering ? 'completed' : (loadingStages.fetching || loadingStages.processing) ? 'pending' : 'active'}`}>
              {!loadingStages.rendering ? <span>✓</span> : (loadingStages.fetching || loadingStages.processing) ? <span>⏳</span> : <div className="mini-spinner"></div>}
              <span>Rendering 3D Scene</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return <div className="alert alert-danger">{error}</div>;
  }

  // Combine all selectable elements for the list
  const allElements = [
    ...sceneData.walls,  // Add walls to the selectable list
    ...sceneData.artworks,
    ...sceneData.boxes,
    ...sceneData.easels,
    ...sceneData.furniture,
    ...(sceneData.structures || [])
  ];

  return (
    <div className="gallery-3d-editor">
      {/* NEW: Header with View Switcher */}
      <div style={{
        position: 'fixed',
        top: '10px',
        right: '10px',
        background: 'rgba(0,0,0,0.8)',
        padding: '10px',
        borderRadius: '8px',
        zIndex: 1001,
        display: 'flex',
        gap: '10px'
      }}>
        <button
          onClick={() => switchView('3d')}
          style={{
            padding: '8px 16px',
            borderRadius: '4px',
            border: 'none',
            cursor: 'pointer',
            background: currentView === '3d' ? '#4f46e5' : '#4a5568',
            color: 'white',
            fontSize: '12px',
            fontWeight: currentView === '3d' ? 'bold' : 'normal'
          }}
        >
          🏛️ 3D View
        </button>
        <button
          onClick={() => window.location.href = `/gallery-wall-planner?collection_id=${collectionId}&view=topdown`}
          style={{
            padding: '8px 16px',
            borderRadius: '4px',
            border: 'none',
            cursor: 'pointer',
            background: '#4a5568',
            color: 'white',
            fontSize: '12px',
            fontWeight: 'normal'
          }}
          title="Top-down floor planner with walls, pods, easels and art placement"
        >
          🔧 Top Down Planner
        </button>
        <button
          onClick={() => window.location.href = `/curator/collections/${collectionId}`}
          style={{
            padding: '8px 16px',
            borderRadius: '4px',
            border: 'none',
            cursor: 'pointer',
            background: '#4a5568',
            color: 'white',
            fontSize: '12px',
            fontWeight: 'normal'
          }}
          title="Go to Collection Admin"
        >
          ⚙️ Admin
        </button>
        <button
          onClick={() => setShowSceneSettings(true)}
          style={{
            padding: '8px 16px',
            borderRadius: '4px',
            border: 'none',
            cursor: 'pointer',
            background: showSceneSettings ? '#4f46e5' : '#4a5568',
            color: 'white',
            fontSize: '12px',
            fontWeight: 'normal'
          }}
          title="3D Scene Settings"
        >
          🎨 Scene
        </button>
        <button
          onClick={() => setShowSideMenu(!showSideMenu)}
          style={{
            padding: '8px 16px',
            borderRadius: '4px',
            border: 'none',
            cursor: 'pointer',
            background: showSideMenu ? '#4f46e5' : '#4a5568',
            color: 'white',
            fontSize: '12px',
            fontWeight: showSideMenu ? 'bold' : 'normal'
          }}
          title="Toggle Side Menu"
        >
          📋 {showSideMenu ? 'Hide' : 'Show'} Menu
        </button>
        <button
          onClick={handleSyncAllArtwork}
          disabled={isSyncing}
          style={{
            padding: '8px 16px',
            borderRadius: '4px',
            border: 'none',
            cursor: isSyncing ? 'wait' : 'pointer',
            background: isSyncing ? '#6b7280' : '#059669',
            color: 'white',
            fontSize: '12px',
            fontWeight: 'normal',
            opacity: isSyncing ? 0.7 : 1
          }}
          title="Recalculate all artwork 3D positions from their wall placements"
        >
          {isSyncing ? '⏳ Syncing...' : '🔄 Sync Art'}
        </button>
      </div>





      {/* Side Panels - Only show in 3D view and when showSideMenu is true */}
      {currentView === '3d' && showSideMenu && (
        <>


          <ListPanel 
            elements={allElements} 
            onSelectElement={(element) => {
              // Just navigate to the element, don't select it
              console.log('🎯 Navigating to element:', element);
              
              // Calculate position to view the element from front
              let posX, posY, posZ;
              
              if (element.type === 'wall_2d') {
                // For walls, position camera in front of the wall using normal vector
                const wallCenterX = (element.wall_center_x || 0) / 12;
                const wallCenterY = (element.wall_center_y || 72) / 12;
                const wallCenterZ = (element.wall_center_z || 0) / 12;
                const wallRotationY = element.wall_rotation_y || 0;
                
                // Calculate wall normal vector from rotation
                const normalX = Math.sin(wallRotationY * Math.PI / 180);
                const normalZ = Math.cos(wallRotationY * Math.PI / 180);
                
                console.log('🎥 Wall normal calculation:', {
                  wallRotationY,
                  normalX: normalX.toFixed(3),
                  normalZ: normalZ.toFixed(3),
                  radians: (wallRotationY * Math.PI / 180).toFixed(3)
                });
                
                // Position camera 6 feet away from wall center in normal direction
                posX = wallCenterX + normalX * 6; // 6 feet in front of wall
                posY = wallCenterY;
                posZ = wallCenterZ + normalZ * 6;
                
                console.log('🎥 Wall camera positioning:', {
                  wallCenter: `(${wallCenterX.toFixed(2)}, ${wallCenterY.toFixed(2)}, ${wallCenterZ.toFixed(2)})`,
                  wallRotation: `${wallRotationY}°`,
                  normalVector: `(${normalX.toFixed(2)}, 0, ${normalZ.toFixed(2)})`,
                  cameraPosition: `(${posX.toFixed(2)}, ${posY.toFixed(2)}, ${posZ.toFixed(2)})`
                });
              } else if (element.type === 'artwork') {
                // For artworks, we need to calculate their actual 3D position
                if (element.parent_element_id) {
                  // Artwork is on a wall - find the wall and calculate position
                  const wall = sceneData.walls?.find(w => w.element_id === element.parent_element_id);
                  if (wall) {
                    // Calculate artwork position relative to wall (same logic as Wall3DBox)
                    const wallCenterX = (wall.wall_center_x || 0) / 12;
                    const wallCenterY = (wall.wall_center_y || wall.height/2) / 12;
                    const wallCenterZ = (wall.wall_center_z || 0) / 12;
                    const wallRotationY = wall.wall_rotation_y || 0;
                    
                    // Calculate offset from wall center (same as Wall3DBox)
                    const offsetXInches = (element.wall_position_x + element.width/2) - (wall.width / 2);
                    const offsetYInches = (wall.height / 2) - (element.wall_position_y + element.height/2);
                    const offsetXFeet = offsetXInches / 12;
                    const offsetYFeet = offsetYInches / 12;
                    
                    // Apply wall rotation to offset
                    const cosRot = Math.cos(wallRotationY * Math.PI / 180);
                    const sinRot = Math.sin(wallRotationY * Math.PI / 180);
                    const rotatedOffsetX = offsetXFeet * cosRot - offsetYFeet * sinRot;
                    const rotatedOffsetZ = offsetXFeet * sinRot + offsetYFeet * cosRot;
                    
                    // Final artwork position in world space
                    posX = wallCenterX + rotatedOffsetX;
                    posY = wallCenterY + offsetYFeet;
                    posZ = wallCenterZ + rotatedOffsetZ;
                    
                    // Calculate wall normal vector for camera positioning
                    const normalX = Math.sin(wallRotationY * Math.PI / 180);
                    const normalZ = Math.cos(wallRotationY * Math.PI / 180);
                    
                    // Position camera 6 feet away from artwork in wall normal direction
                    posX = posX + normalX * 6;
                    posZ = posZ + normalZ * 6;
                    
                    console.log('🎨 Artwork on wall - calculated position:', {
                      wallCenter: `(${wallCenterX.toFixed(2)}, ${wallCenterY.toFixed(2)}, ${wallCenterZ.toFixed(2)})`,
                      offset: `(${offsetXFeet.toFixed(2)}, ${offsetYFeet.toFixed(2)})`,
                      rotatedOffset: `(${rotatedOffsetX.toFixed(2)}, ${rotatedOffsetZ.toFixed(2)})`,
                      artworkPosition: `(${(posX + normalX * 6).toFixed(2)}, ${posY.toFixed(2)}, ${(posZ + normalZ * 6).toFixed(2)})`,
                      normalVector: `(${normalX.toFixed(2)}, 0, ${normalZ.toFixed(2)})`,
                      cameraPosition: `(${posX.toFixed(2)}, ${posY.toFixed(2)}, ${posZ.toFixed(2)})`
                    });
                  } else {
                    // Fallback to direct position if wall not found
                    posX = (element.position_x || 0) / 12;
                    posY = (element.position_y || 0) / 12;
                    posZ = (element.position_z || 0) / 12;
                  }
                } else {
                  // Unplaced artwork - use direct position
                  posX = (element.position_x || 0) / 12;
                  posY = (element.position_y || 0) / 12;
                  posZ = (element.position_z || 0) / 12;
                }
                
                // For unplaced artworks, position camera 6 feet in front
                if (!element.parent_element_id) {
                  posZ -= 6; // 6 feet in front
                  
                  console.log('🎥 Final camera position for unplaced artwork:', {
                    artworkPosition: `(${posX.toFixed(2)}, ${posY.toFixed(2)}, ${(posZ + 6).toFixed(2)})`,
                    cameraPosition: `(${posX.toFixed(2)}, ${posY.toFixed(2)}, ${posZ.toFixed(2)})`
                  });
                }
              } else {
                // Other element types
                posX = (element.position_x || 0) / 12;
                posY = (element.position_y || 0) / 12;
                posZ = (element.position_z || 0) / 12 + 8;
              }
              
              // Calculate camera rotation to look at the element
              let cameraRotation = '0 0 0';
              console.log('🎥 Starting rotation calculation for element type:', element.type);
              if (element.type === 'wall_2d') {
                console.log('🎥 Processing wall_2d rotation...');
                // For walls, calculate rotation to look at wall center
                const wallCenterX = (element.wall_center_x || 0) / 12;
                const wallCenterY = (element.wall_center_y || 72) / 12;
                const wallCenterZ = (element.wall_center_z || 0) / 12;
                
                // Calculate angle to look at wall center
                const deltaX = wallCenterX - posX;
                const deltaZ = wallCenterZ - posZ;
                const angleY = Math.atan2(deltaX, deltaZ) * 180 / Math.PI;
                
                cameraRotation = `0 ${angleY} 0`;
                console.log('🎥 Wall camera rotation:', {
                  deltaX: deltaX.toFixed(2),
                  deltaZ: deltaZ.toFixed(2),
                  angleY: angleY.toFixed(2),
                  cameraRotation: cameraRotation
                });
              } else if (element.type === 'artwork' && element.parent_element_id) {
                console.log('🎥 Processing artwork rotation...');
                // For artworks on walls, calculate rotation to look at artwork
                const wall = sceneData.walls?.find(w => w.element_id === element.parent_element_id);
                if (wall) {
                  console.log('🎥 Found wall for artwork:', wall.element_id);
                  // Calculate artwork position (same as before)
                  const wallCenterX = (wall.wall_center_x || 0) / 12;
                  const wallCenterY = (wall.wall_center_y || wall.height/2) / 12;
                  const wallCenterZ = (wall.wall_center_z || 0) / 12;
                  const wallRotationY = wall.wall_rotation_y || 0;
                  
                  // Calculate offset from wall center
                  const offsetXInches = (element.wall_position_x + element.width/2) - (wall.width / 2);
                  const offsetYInches = (wall.height / 2) - (element.wall_position_y + element.height/2);
                  const offsetXFeet = offsetXInches / 12;
                  const offsetYFeet = offsetYInches / 12;
                  
                  // Apply wall rotation to offset
                  const cosRot = Math.cos(wallRotationY * Math.PI / 180);
                  const sinRot = Math.sin(wallRotationY * Math.PI / 180);
                  const rotatedOffsetX = offsetXFeet * cosRot - offsetYFeet * sinRot;
                  const rotatedOffsetZ = offsetXFeet * sinRot + offsetYFeet * cosRot;
                  
                  // Final artwork position in world space
                  const artworkX = wallCenterX + rotatedOffsetX;
                  const artworkY = wallCenterY + offsetYFeet;
                  const artworkZ = wallCenterZ + rotatedOffsetZ;
                  
                  // Calculate angle to look at artwork
                  const deltaX = artworkX - posX;
                  const deltaZ = artworkZ - posZ;
                  const angleY = Math.atan2(deltaX, deltaZ) * 180 / Math.PI;
                  
                  cameraRotation = `0 ${angleY} 0`;
                  
                  console.log('🎥 Artwork camera rotation calculation:', {
                    artworkX: artworkX.toFixed(2),
                    artworkZ: artworkZ.toFixed(2),
                    posX: posX.toFixed(2),
                    posZ: posZ.toFixed(2),
                    deltaX: deltaX.toFixed(2),
                    deltaZ: deltaZ.toFixed(2),
                    angleY: angleY.toFixed(2),
                    finalRotation: cameraRotation
                  });
                  
                  console.log('🎥 Artwork camera rotation:', {
                    artworkPosition: `(${artworkX.toFixed(2)}, ${artworkY.toFixed(2)}, ${artworkZ.toFixed(2)})`,
                    cameraPosition: `(${posX.toFixed(2)}, ${posY.toFixed(2)}, ${posZ.toFixed(2)})`,
                    deltaX: deltaX.toFixed(2),
                    deltaZ: deltaZ.toFixed(2),
                    angleY: angleY.toFixed(2)
                  });
                }
              }
              
              // Move camera to view the element
              console.log('🎥 Final camera values before moveCamera:');
              console.log('🎥 Position:', `${posX} ${posY + 2} ${posZ}`);
              console.log('🎥 Rotation:', cameraRotation);
              console.log('🎥 Element type:', element.type);
              
              // Move camera to the calculated position
              moveCamera(`${posX} ${posY + 2} ${posZ}`, cameraRotation);
              
            }}
            selectedElement={null}
            onClose={() => {}}
            onNavigateToWallPlanner={() => window.location.href = `/gallery-wall-planner?collection_id=${collectionId}`}
          />
        </>
      )}

      {/* Main Content Area - 3D View Only */}
      {currentView === '3d' && (
        <>
          {/* A-Frame Scene - Your existing 3D view */}
          <a-scene 
            ref={sceneRef}
            vr-mode-ui="enabled: false" 
            loading-screen="enabled: false"
            background={`color: ${sceneSettings.backgroundColor}`}
            stats="false"
            look-controls={isSelectMode ? "enabled: false" : "enabled: true"}
            wasd-controls={isSelectMode ? "enabled: false" : "enabled: true"}
            onLoad={() => {
              console.log('🎬 A-Frame scene loaded');
              setTimeout(() => {
                const camera = document.querySelector('#camera');
                console.log('🎥 Camera after scene load:', camera);
                if (camera) {
                  console.log('🎥 Camera position:', camera.getAttribute('position'));
                  console.log('🎥 Camera object3D:', camera.object3D);
                }
              }, 1000);
            }}
          >
            {/* Let A-Frame create its default camera */}
            
            {/* Lighting - Dynamic based on scene settings */}
            <a-light type="ambient" color="#606060" intensity={sceneSettings.ambientLightIntensity} />
            <a-light type="directional" position="10 20 10" intensity={sceneSettings.directionalLightIntensity} color="#ffffff" />
            <a-light type="directional" position="-10 15 -10" intensity={sceneSettings.directionalLightIntensity * 0.4} color="#ffffff" />
            <a-light type="point" position="0 15 0" intensity="0.3" color="#ffffff" distance="50" />
            
            {/* Floor */}
            {floorData ? (
              <a-plane
                position={`${(floorData.position_x || 0) / 12} ${(floorData.position_y || -0.5) / 12} ${(floorData.position_z || 0) / 12}`}
                rotation="-90 0 0"
                width={`${(floorData.width || 2000) / 12}`}
                height={`${(floorData.depth || 2000) / 12}`}
                material={floorData.texture_url ? `src: ${floorData.texture_url}; repeat: 20 20` : `color: ${floorData.color || '#8B4513'}`}
                shadow="receive: true"
                onLoad={() => {
                  console.log('🏠 Floor loaded:', {
                    position: `${(floorData.position_x || 0) / 12} ${(floorData.position_y || -0.5) / 12} ${(floorData.position_z || 0) / 12}`,
                    dimensions: `${(floorData.width || 2000) / 12} x ${(floorData.depth || 2000) / 12}`,
                    rotation: '-90 0 0',
                    material: floorData.texture_url ? `texture: ${floorData.texture_url}` : `color: ${floorData.color || '#8B4513'}`,
                    floorData: floorData
                  });
                }}
              />
            ) : (
              <a-plane
                position="0 -0.5 0"
                rotation="-90 0 0"
                width="200"
                height="200"
                material="color: #8B4513; repeat: 20 20"
                shadow="receive: true"
                onLoad={() => {
                  console.log('🏠 Default floor loaded (no floor data)');
                }}
              />
            )}
            
            {/* STATIC: Gallery Infrastructure (doesn't change) */}
            {galleryId && <GalleryInfrastructure galleryId={galleryId} />}
            
            {/* NEW: 3D Wall Boxes with artwork textures */}
            {render3DWalls()}
            
            {/* Easels with artwork */}
            {renderEaselsWithArtwork()}
            
            {/* Structural elements (columns, solid walls) */}
            {renderStructures()}
            
                        {/* Furniture - Working version with proper selection */}
            {sceneData.furniture.map((furniture, index) => {
              const isSelected = selectedElement && selectedElement.element_id === furniture.element_id;
              
              // Get the actual scale factors from database
              const scaleX = parseFloat(furniture.scale_x) || 1;
              const scaleY = parseFloat(furniture.scale_y) || 1;
              const scaleZ = parseFloat(furniture.scale_z) || 1;
              
              // Calculate the actual size (no double scaling)
              const actualWidth = (parseFloat(furniture.width) || 24) / 12; // Convert to feet only
              const actualDepth = (parseFloat(furniture.depth) || 24) / 12;
              const actualHeight = (parseFloat(furniture.height) || 24) / 12;
              
              const posX = (parseFloat(furniture.position_x) || 0) / 12;
              const posY = (parseFloat(furniture.position_y) || 0) / 12;
              const posZ = (parseFloat(furniture.position_z) || 0) / 12;
              
              console.log(`🪑 Furniture ${furniture.element_id} - Scale: ${scaleX}, Size: ${actualWidth}x${actualDepth}x${actualHeight} feet`);
              
              return (
                <a-entity key={`furniture-${furniture.element_id || index}`}>
                  {/* GLB Model - Visual representation */}
                  <a-entity
                    id={`element-${furniture.element_id || index}`}
                    position={`${posX} ${posY} ${posZ}`}
                    rotation={`${furniture.rotation_x || 0} ${furniture.rotation_y || 0} ${furniture.rotation_z || 0}`}
                    scale={`${scaleX} ${scaleY} ${scaleZ}`}
                    gltf-model={furniture.source}
                    material="shader: standard; transparent: true; opacity: 1.0"
                    onLoad={() => {
                      if (isSelected) {
                        console.log(`🪑 Selected furniture ${furniture.element_id} loaded:`, {
                          position: `${posX}, ${posY}, ${posZ}`,
                          rotation: `${furniture.rotation_x || 0}, ${furniture.rotation_y || 0}, ${furniture.rotation_z || 0}`,
                          scale: `${scaleX}, ${scaleY}, ${scaleZ}`
                        });
                      }
                    }}
                  />

                  {/* Clickable box that matches the furniture size */}
                  <a-box
                    position={`${posX} ${posY + actualHeight/2} ${posZ}`}
                    width={actualWidth}
                    height={actualHeight}
                    depth={actualDepth}
                    material="color: red; opacity: 0.0; transparent: true"
                    className="clickable movable furniture-hitbox"
                    onClick={(e) => {
                      console.log(`🪑 Furniture ${furniture.element_id} clicked!`);
                      e.stopPropagation();
                      handleSelectElement(furniture, e.ctrlKey || e.metaKey);
                    }}
                    data-element-id={furniture.element_id}
                    visible={true}
                  />

                  {/* Selection indicators */}
                  {isSelected && (
                    <>
                      {/* Rotating selection box above furniture */}
                      <a-box
                        position={`${posX} ${posY + actualHeight + 1} ${posZ}`}
                        width="3" height="3" depth="3"
                        material="color: yellow; opacity: 0.2; transparent: true"
                        animation="property: rotation; to: 0 360 0; loop: true; dur: 4000"
                      />
                      
                      {/* Instructions text */}
                      <a-text
                        value="Use Arrow Keys to Move"
                        position={`${posX} ${posY + actualHeight + 2} ${posZ}`}
                        align="center"
                        color="yellow"
                        scale="2 2 2"
                      />
                      
                      {/* Debug wireframe showing clickable area */}
                      <a-box
                        position={`${posX} ${posY + actualHeight/2} ${posZ}`}
                        width={actualWidth}
                        height={actualHeight}
                        depth={actualDepth}
                        material="color: yellow; opacity: 0.1; transparent: true"
                        wireframe="true"
                      />
                    </>
                  )}
                </a-entity>
              );
            })}
            
            {/* Gallery floor for reference */}
            <a-plane
              width="200"
              height="200"
              position="0 -0.1 0"
              rotation="-90 0 0"
              material="color: #e8e8e8; opacity: 0.3; transparent: true; depthTest: true; depthWrite: true"
              className="gallery-floor"
            />
            
          </a-scene>
        </>
      )}

      {/* Help Modal */}
      {showHelp && (
        <div style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'rgba(0,0,0,0.9)',
          color: 'white',
          padding: '30px',
          borderRadius: '10px',
          zIndex: 2000,
          maxWidth: '400px'
        }}>
          <h3>⌨️ Keyboard Shortcuts</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '8px', fontSize: '14px' }}>
            <strong>Arrow Keys</strong><span>Move selected item (12 inches)</span>
            <strong>Shift + Arrows</strong><span>Fine move (2 inches)</span>
            <strong>Ctrl + Left/Right</strong><span>Rotate selected item (15 degrees)</span>
            <strong>Shift + Ctrl + Left/Right</strong><span>Fine rotate (2 degrees)</span>
            <strong>Page Up/Down</strong><span>Move up/down (Y-axis)</span>
            <strong>Delete</strong><span>Delete selected item</span>
            <strong>Escape</strong><span>Deselect item</span>
            <strong>?</strong><span>Show/hide this help</span>
          </div>
          
          <button
            onClick={() => setShowHelp(false)}
            style={{
              marginTop: '20px',
              background: '#4a5568',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Close
          </button>
        </div>
      )}

      {/* 3D Scene Settings Modal */}
      {showSceneSettings && (
        <SceneSettings
          collectionId={collectionId}
          currentSettings={sceneSettings}
          onSettingsChange={async (newSettings) => {
            setSceneSettings(newSettings);
            // Update the A-Frame scene background immediately
            const scene = document.querySelector('a-scene');
            if (scene) {
              scene.setAttribute('background', `color: ${newSettings.backgroundColor}`);
            }
            // Save settings to database
            try {
              await saveSceneSettings(collectionId, newSettings);
              console.log('✅ Scene settings saved');
            } catch (error) {
              console.error('❌ Error saving scene settings:', error);
            }
          }}
          onFloorUpdate={(newFloorData) => {
            setFloorData(newFloorData);
          }}
          onClose={() => setShowSceneSettings(false)}
        />
      )}
    </div>
  );
};

export default Gallery3DEditor;